// src/core/audio-recorder.js — wrapper sobre MediaRecorder para grabar
// arengas de hasta 20 segundos.
//
// FASE 4 · F1.4.a. Auto-detecta el MIME soportado por el browser (WebM/Opus
// en Chrome/Firefox/Android; MP4/AAC en Safari iOS), graba a 128 kbps mono
// y devuelve un blob listo para subir a Storage.
//
// Bitrate: 128 kbps Opus (subido 28-may-2026 desde 48 kbps). El default
// 48 kbps optimizado para voz dejaba cánticos y música "lavados". Los
// cánticos son contenido permanente del feed (spec Arengas v1.2 §2) — vale
// la pena el ~3x en tamaño (archivo de 20s: ~80KB → ~320KB).
//
// Errores expuestos como Error con .code estable para que la UI traduzca:
//   - 'mic_permission_denied' : el usuario rechazó el permiso.
//   - 'mic_unavailable'       : el dispositivo no tiene micrófono.
//   - 'unsupported_browser'   : ni MediaRecorder ni ningún MIME soportado.

// Orden invertido el 28-may-2026 (bloque calidad arengas): MP4/AAC primero,
// Opus segundo. AAC-LC suena mejor para música/cantos que Opus a igual bitrate
// en la mayoría de los browsers móviles. Si el browser no soporta MP4, cae
// a Opus (Firefox, Android viejos). El bitrate sigue siendo 128 kbps.
const PREFERRED_MIME_TYPES = [
  'audio/mp4;codecs=mp4a.40.2',
  'audio/mp4',
  'audio/webm;codecs=opus',
  'audio/webm',
]

const MAX_DURATION_SECONDS = 20
const MAX_SIZE_BYTES = 5 * 1024 * 1024
const AUDIO_BITS_PER_SECOND = 128000

// Helpers de inspección — útiles para tests y para la UI antes de pedir mic.
export const getPreferredMimeTypes = () => [...PREFERRED_MIME_TYPES]
export const getMaxDuration = () => MAX_DURATION_SECONDS
export const getMaxSize = () => MAX_SIZE_BYTES

// detectMimeType(MediaRecorderImpl?) — primero soportado de PREFERRED_MIME_TYPES.
// El override es para tests (se le pasa un fake con isTypeSupported).
export const detectMimeType = (Impl) => {
  const MR = Impl || (typeof MediaRecorder !== 'undefined' ? MediaRecorder : null)
  if (!MR || typeof MR.isTypeSupported !== 'function') return null
  for (const mime of PREFERRED_MIME_TYPES) {
    try { if (MR.isTypeSupported(mime)) return mime } catch { /* sigue */ }
  }
  return null
}

const createMicError = (code, message) => {
  const e = new Error(message || code)
  e.code = code
  return e
}

// startRecording(opts?) — pide permiso, abre stream, arranca grabador.
// Devuelve un handle { stop(), cancel(), stream, mimeType, startedAt, dispose() }.
// opts.onTick(seconds) opcional — la UI lo usa para actualizar el timer.
// opts.autoStopAfterMs opcional (default 20_000) — corta solo al límite.
// Si MediaRecorder no existe o ningún MIME es soportado → 'unsupported_browser'.
// Si el usuario rechaza permiso → 'mic_permission_denied'.
// Si no hay micrófono → 'mic_unavailable'.
export const startRecording = async (opts = {}) => {
  const navObj = typeof navigator !== 'undefined' ? navigator : null
  if (!navObj?.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
    throw createMicError('unsupported_browser', 'Tu navegador no soporta grabación de audio.')
  }
  const mimeType = detectMimeType()
  if (!mimeType) {
    throw createMicError('unsupported_browser', 'Tu navegador no soporta ningún formato de audio compatible.')
  }

  // Apagamos los 3 filtros default del browser ("modo llamada de WhatsApp"):
  // echoCancellation + noiseSuppression + autoGainControl degradan música y
  // canto. Si el browser rechaza la config (OverconstrainedError), caemos al
  // default sin romper la grabación.
  let stream
  const constraintsOptimas = {
    audio: {
      channelCount: 1,
      echoCancellation: false,
      noiseSuppression: false,
      autoGainControl: false,
    },
  }
  try {
    stream = await navObj.mediaDevices.getUserMedia(constraintsOptimas)
  } catch (err) {
    const name = err?.name || ''
    if (name === 'NotAllowedError' || name === 'SecurityError') {
      throw createMicError('mic_permission_denied', 'Permiso de micrófono denegado.')
    }
    if (name === 'OverconstrainedError') {
      // Fallback: el browser no acepta los flags de calidad — intentamos con
      // la config básica (filtros del browser activos como antes).
      try {
        stream = await navObj.mediaDevices.getUserMedia({ audio: { channelCount: 1 } })
      } catch (err2) {
        const name2 = err2?.name || ''
        if (name2 === 'NotAllowedError' || name2 === 'SecurityError') {
          throw createMicError('mic_permission_denied', 'Permiso de micrófono denegado.')
        }
        throw createMicError('mic_unavailable', err2?.message || 'Mic no disponible.')
      }
    } else if (name === 'NotFoundError') {
      throw createMicError('mic_unavailable', 'No encontramos un micrófono disponible.')
    } else {
      throw createMicError('mic_unavailable', err?.message || 'Mic no disponible.')
    }
  }

  const chunks = []
  // Defensivo: si el browser rechaza el bitrate solicitado (raro, pero algunos
  // builds viejos / device exóticos), fallback al default del browser sin
  // romper la grabación.
  let recorder
  try {
    recorder = new MediaRecorder(stream, { mimeType, audioBitsPerSecond: AUDIO_BITS_PER_SECOND })
  } catch (_e) {
    recorder = new MediaRecorder(stream, { mimeType })
  }
  recorder.addEventListener('dataavailable', (ev) => {
    if (ev.data && ev.data.size > 0) chunks.push(ev.data)
  })

  const startedAt = Date.now()
  const autoStopAfterMs = Number.isFinite(opts.autoStopAfterMs) ? opts.autoStopAfterMs : MAX_DURATION_SECONDS * 1000
  let tickHandle = null
  let autoStopHandle = null
  let resolved = false

  if (typeof opts.onTick === 'function') {
    tickHandle = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startedAt) / 1000)
      try { opts.onTick(Math.min(elapsed, MAX_DURATION_SECONDS)) } catch { /* swallow */ }
    }, 250)
  }

  const cleanup = () => {
    if (tickHandle) { clearInterval(tickHandle); tickHandle = null }
    if (autoStopHandle) { clearTimeout(autoStopHandle); autoStopHandle = null }
    for (const track of stream.getTracks()) {
      try { track.stop() } catch { /* swallow */ }
    }
  }

  // Promesa de finalización: se arma UNA sola vez y se resuelve cuando el
  // recorder emite 'stop', sin importar QUIÉN lo paró — auto-corte al límite
  // (setTimeout de abajo), click "Finalizar" (handle.stop) o cancel.
  //
  // Bug grabación 20s (jun-2026): antes el listener 'stop' que ensambla el
  // blob se registraba recién DENTRO de stop(). El auto-corte interno paraba
  // el recorder sin pasar por stop(), así que nadie ensamblaba el blob → la
  // grabación quedaba colgada, y un stop() posterior (poll o click "Finalizar")
  // encontraba el recorder ya 'inactive' y tiraba InvalidStateError. Registrar
  // el listener al inicio desacopla "parar" de "ensamblar": cualquier camino
  // que pare el recorder produce el blob.
  const donePromise = new Promise((resolve, reject) => {
    recorder.addEventListener('stop', () => {
      resolved = true
      cleanup()
      const blob = new Blob(chunks, { type: mimeType })
      const duracionMs = Date.now() - startedAt
      if (blob.size > MAX_SIZE_BYTES) {
        reject(createMicError('size_exceeded', `El audio supera ${MAX_SIZE_BYTES} bytes.`))
        return
      }
      resolve({ blob, mimeType, duracionMs })
    }, { once: true })
    recorder.addEventListener('error', (ev) => {
      resolved = true
      cleanup()
      reject(createMicError('recorder_error', ev?.error?.message || 'Falla del grabador.'))
    }, { once: true })
  })
  // Si nadie llega a esperar la promesa (ej: cancel mid-grabación), evitamos
  // el warning de unhandled rejection. Los awaiters reales siguen recibiendo
  // el rechazo por su propio await.
  donePromise.catch(() => { /* swallow unhandled */ })

  // stop() es idempotente: dispara recorder.stop() solo si sigue activo y en
  // todos los casos devuelve la MISMA promesa de finalización. Llamarlo dos
  // veces (auto-corte + click "Finalizar") ya no rompe.
  const stop = () => {
    if (recorder.state !== 'inactive') {
      try { recorder.stop() } catch { /* el listener stop/error resuelve */ }
    }
    return donePromise
  }

  if (autoStopAfterMs > 0) {
    autoStopHandle = setTimeout(() => {
      if (resolved || recorder.state === 'inactive') return
      try { recorder.stop() } catch { /* lo maneja el listener de stop */ }
      if (typeof opts.onAutoStop === 'function') {
        try { opts.onAutoStop() } catch { /* swallow */ }
      }
    }, autoStopAfterMs)
  }

  recorder.start()

  return {
    stream,
    mimeType,
    startedAt,
    stop,
    cancel: () => {
      if (resolved) return
      if (recorder.state !== 'inactive') {
        // El listener 'stop' resuelve donePromise (nadie la espera) y limpia.
        try { recorder.stop() } catch { resolved = true; cleanup() }
      } else {
        resolved = true
        cleanup()
      }
    },
    dispose: cleanup,
  }
}

// Helper para mapear MIME → extensión usada en el path del bucket.
export const extensionForMime = (mimeType) => {
  if (!mimeType) return 'bin'
  const head = String(mimeType).split(';')[0].trim().toLowerCase()
  if (head === 'audio/webm') return 'webm'
  if (head === 'audio/mp4') return 'm4a'
  if (head === 'audio/mpeg') return 'mp3'
  if (head === 'audio/ogg') return 'ogg'
  return 'bin'
}
